import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { 
  getNotifications, 
  getUnreadNotificationCount, 
  markNotificationAsRead, 
  markAllNotificationsAsRead,
  deleteNotification
} from '../services/notificationService';
import { 
  initializeSocket, 
  subscribeToEvent, 
  unsubscribeFromEvent,
  subscribeToReportEvents,
  unsubscribeFromReportEvents,
  closeSocket 
} from '../services/socketService';
import showNotification from '../components/common/Notification';

// Eski versiyon için geriye uyumluluk
const STORAGE_KEY = 'viewedReportNotifications';
const NOTIFICATION_EXPIRY_DAYS = 2; // 2 gün sonra bildirimler gizlenecek

const NotificationsContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

const migrateOldStorageFormat = (oldData) => {
  // Eski format bir array ise, yeni formata dönüştür
  if (Array.isArray(oldData)) {
    const now = new Date().getTime();
    return oldData.map(id => [
      id,
      {
        viewed: true,
        viewedAt: now,
        reportCreatedAt: now
      }
    ]);
  }
  return oldData;
};

export const NotificationsProvider = ({ children }) => {
  const { token } = useAuth();
  const [viewedNotifications, setViewedNotifications] = useState(new Map());
  const [unreadCount, setUnreadCount] = useState(0);
  const [allReports, setAllReports] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [websocketConnected, setWebsocketConnected] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [lastReportEvent, setLastReportEvent] = useState(null);
  const initialFetchDone = useRef(false);
  const tokenRef = useRef(token);
  const prevTokenRef = useRef(token);

  // Token değişikliğinde referansını güncelle
  useEffect(() => {
    // Token değiştiyse güncelle
    if (token !== prevTokenRef.current) {
      tokenRef.current = token;
      prevTokenRef.current = token;
      // Token değiştiğinde initialFetch flag'i resetle
      initialFetchDone.current = false;
    }
  }, [token]);

  // Load viewed notifications from localStorage on mount (for backward compatibility)
  useEffect(() => {
    try {
      const storedNotifications = localStorage.getItem(STORAGE_KEY);
      if (storedNotifications) {
        const parsedData = JSON.parse(storedNotifications);
        const migratedData = migrateOldStorageFormat(parsedData);
        setViewedNotifications(new Map(migratedData));
      }
    } catch (error) {
      console.warn('Error loading notifications from storage:', error);
      localStorage.removeItem(STORAGE_KEY);
      setViewedNotifications(new Map());
    }
  }, []);

  // Yeni bildirim geldiğinde
  const handleNewNotification = useCallback((notification) => {
    // Yeni bildirimi listeye ekle
    setNotifications(prev => [notification, ...prev]);
    
    // Unread sayısını güncelle
    setUnreadCount(prev => prev + 1);
    
    // Bildirim toast'u göster
    showNotification(notification.title, notification.type);
  }, []);
  
  // Push report socket events to consumers (Dashboard live refresh)
  const handleReportSocketEvent = useCallback((eventName, payload) => {
    setLastReportEvent({
      eventName,
      data: payload,
      at: Date.now(),
    });
  }, []);
  
  // Unread count API'den çekme
  const fetchUnreadCount = useCallback(async () => {
    if (!tokenRef.current) return;
    
    try {
      const count = await getUnreadNotificationCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Unread count alınırken hata:', error);
    }
  }, []);
  
  // Bildirimleri API'den çekme
  const fetchNotifications = useCallback(async (pageParam = 1, append = false) => {
    if (!tokenRef.current || loading) return;
    
    setLoading(true);
    try {
      const response = await getNotifications({
        page: pageParam, 
        limit
      });
      
      const { data, pagination } = response;
      
      if (append) {
        setNotifications(prev => [...prev, ...data]);
      } else {
        setNotifications(data);
      }
      
      setHasMore(pagination.page < pagination.pages);
      setPage(pagination.page);
      setUnreadCount(response.unreadCount);
    } catch (error) {
      console.error('Bildirimler alınırken hata:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, limit]);
  
  // WebSocket bağlantısı kurulamadığında düzenli polling ile bildirimleri al
  useEffect(() => {
    // WebSocket bağlantısı varsa polling'i durdur
    if (websocketConnected && pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
      return;
    }
    
    // WebSocket bağlantısı yoksa ve polling başlamadıysa, polling başlat
    if (!websocketConnected && !pollingInterval && tokenRef.current) {
      const interval = setInterval(() => {
        fetchUnreadCount();
        fetchNotifications(1, false);
      }, 30000); // 30 saniyede bir güncelle
      
      setPollingInterval(interval);
      
      return () => {
        clearInterval(interval);
      };
    }
    
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [websocketConnected, pollingInterval, fetchUnreadCount, fetchNotifications]);
  
  // WebSocket bağlantısını kurma
  useEffect(() => {
    if (!token) return;
    
    let currentSocket = null;
    
    const setupWebSocket = () => {
      if (!token) {
        return null;
      }
      
      const socket = initializeSocket(token);
      
      // If socket initialization returned null, it means connection failed
      if (!socket) {
        setWebsocketConnected(false);
        return null;
      }
      
      // Bildirim olayını dinle
      subscribeToEvent('notification', handleNewNotification);

      // Report lifecycle events for dashboard sync
      subscribeToReportEvents(handleReportSocketEvent);
      
      // Bağlantı kesildiğinde
      subscribeToEvent('disconnect', () => {
        setWebsocketConnected(false);
      });
      
      // Bağlantı yeniden kurulduğunda
      subscribeToEvent('connect', () => {
        setWebsocketConnected(true);
        subscribeToReportEvents(handleReportSocketEvent);
        // Bağlantı kurulduğunda unread sayısını güncelle
        fetchUnreadCount();
      });
      
      // Bağlantı hatası olduğunda
      subscribeToEvent('connect_error', () => {
        setWebsocketConnected(false);
      });
      
      return socket;
    };
    
    currentSocket = setupWebSocket();
    
    // If we couldn't establish a WebSocket connection, immediately set websocketConnected to false
    if (!currentSocket) {
      setWebsocketConnected(false);
    }
    
    return () => {
      if (currentSocket) {
        unsubscribeFromEvent('notification');
        unsubscribeFromReportEvents();
        unsubscribeFromEvent('disconnect');
        unsubscribeFromEvent('connect');
        unsubscribeFromEvent('connect_error');
        closeSocket();
      }
    };
  }, [token, fetchUnreadCount, handleNewNotification, handleReportSocketEvent]);

  // İlk yüklemede verileri bir kez çek
  useEffect(() => {
    const shouldFetch = token && !initialFetchDone.current;
    
    if (shouldFetch) {
      const performInitialFetch = async () => {
        try {
          initialFetchDone.current = true; // Önce true yap ki asenkron işlemler sırasında tekrar çağrılmasın
          await fetchUnreadCount();
          await fetchNotifications(1, false);
        } catch (error) {
          console.error('Initial fetch error:', error);
          // Hata durumunda flag'i düzelt
          initialFetchDone.current = false;
        }
      };
      
      performInitialFetch();
    }
  }, [token, fetchUnreadCount, fetchNotifications]);

  // Sayfalama için daha fazla bildirim yükleme
  const loadMoreNotifications = useCallback(() => {
    if (!loading && hasMore) {
      fetchNotifications(page + 1, true);
    }
  }, [loading, hasMore, page, fetchNotifications]);

  // Bildirimi okundu olarak işaretleme
  const markAsRead = useCallback(async (notificationId) => {
    if (!tokenRef.current) return;
    
    try {
      await markNotificationAsRead(notificationId);
      
      // Yerel state güncelleme
      setNotifications(prev => 
        prev.map(notification => 
          notification._id === notificationId 
            ? { ...notification, isRead: true } 
            : notification
        )
      );
      
      // Unread count güncelleme
      fetchUnreadCount();
    } catch (error) {
      console.error('Bildirim okundu olarak işaretlenirken hata:', error);
    }
  }, [fetchUnreadCount]);

  // Tüm bildirimleri okundu olarak işaretleme
  const markAllAsRead = useCallback(async () => {
    if (!tokenRef.current) return;
    
    try {
      await markAllNotificationsAsRead();
      
      // Yerel state güncelleme
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      );
      
      // Unread count sıfırlama
      setUnreadCount(0);
    } catch (error) {
      console.error('Tüm bildirimler okundu olarak işaretlenirken hata:', error);
    }
  }, []);

  // Bildirimi silme
  const removeNotification = useCallback(async (notificationId) => {
    if (!tokenRef.current) return;
    
    try {
      await deleteNotification(notificationId);
      
      // Yerel state güncelleme
      setNotifications(prev => 
        prev.filter(notification => notification._id !== notificationId)
      );
      
      // Eğer silinmiş bildirim okunmamış ise unread count güncelle
      const notification = notifications.find(n => n._id === notificationId);
      if (notification && !notification.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Bildirim silinirken hata:', error);
    }
  }, [notifications]);

  // Geriye uyumluluk için aşağıdaki fonksiyonları da koruyoruz

  const markReportsAsViewed = (reports) => {
    const now = new Date().getTime();
    const newViewedNotifications = new Map(viewedNotifications);
    
    reports.forEach(report => {
      if (!newViewedNotifications.has(report.id)) {
        newViewedNotifications.set(report.id, {
          viewed: true,
          viewedAt: now,
          reportCreatedAt: report.createdAt || now
        });
      }
    });
    
    try {
      const storageData = Array.from(newViewedNotifications.entries());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
      setViewedNotifications(newViewedNotifications);
    } catch (error) {
      console.warn('Error saving notifications to storage:', error);
    }
  };

  const getVisibleReports = () => {
    // If there are no reports, return an empty array to avoid showing notification count
    if (!allReports || allReports.length === 0) {
      return [];
    }
    
    const now = new Date().getTime();
    return allReports.filter(report => {
      const viewedData = viewedNotifications.get(report.id);
      if (!viewedData) return true;
      
      const viewedTime = new Date(viewedData.viewedAt).getTime();
      const expiryTime = viewedTime + (NOTIFICATION_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      
      return now < expiryTime;
    });
  };

  const isReportNew = (reportId) => {
    return !viewedNotifications.has(reportId);
  };

  const updateReports = useCallback((reports) => {
    setAllReports(reports);
  }, []);

  const value = {
    // Yeni API'ye bağlı bildirim fonksiyonları
    notifications,
    unreadCount,
    loading,
    hasMore,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    removeNotification,
    fetchNotifications,
    websocketConnected,
    lastReportEvent,

    // Geriye uyumluluk için
    viewedNotifications,
    allReports,
    markReportsAsViewed,
    updateReports,
    getVisibleReports,
    isReportNew,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsContext; 